/**
 * WebSocket client for the per-project collab room.
 *
 *   URL: <wsBase>/ws/projects/<projectId>/?token=<access>
 *
 * Responsibilities:
 *   - Open/close the socket, exponential-backoff reconnect (≤ 10 attempts).
 *   - Send heartbeats every 25 s so the server-side presence Redis entry
 *     doesn't expire.
 *   - Coalesce cursor messages: while disconnected we only keep the latest
 *     cursor position (no point flushing a stale trail on reconnect).
 *   - On close-code 4401 (bad token), refresh once and reconnect; two 4401s
 *     in a row → bubble up as `closed`.
 *
 * The HTTP API base is `VITE_WEB_AUTH_API_BASE` (e.g. `https://api.example.com`).
 * We swap the scheme to ws/wss. Empty base → same-origin (Vite dev-proxy
 * handles `/ws/` upgrades).
 */

import { getAccessToken, refreshAccessToken } from '../auth/webAuthClient'
import type { ClientEvent, ServerEvent, WsStatus } from './types'

interface ConnectOptions {
  projectId: string
  onEvent: (ev: ServerEvent) => void
  onStatus?: (status: WsStatus) => void
}

export interface WsHandle {
  send(ev: ClientEvent): void
  close(): void
}

const HEARTBEAT_MS = 25_000
const MAX_RECONNECT_ATTEMPTS = 10

function wsBaseUrl(): string {
  const raw = (import.meta.env.VITE_WEB_AUTH_API_BASE ?? '') as string
  if (!raw) {
    // Same-origin: derive ws scheme from window.location
    const loc = window.location
    const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${scheme}//${loc.host}`
  }
  const stripped = raw.replace(/\/+$/, '').replace(/\/api$/, '')
  return stripped
    .replace(/^http:/, 'ws:')
    .replace(/^https:/, 'wss:')
}

export function connect({ projectId, onEvent, onStatus }: ConnectOptions): WsHandle {
  let socket: WebSocket | null = null
  let closed = false
  let attempt = 0
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let consecutive4401 = 0

  // Outgoing buffer: pending non-cursor events + at most one cursor event
  // (the most recent — older positions are stale).
  const buffer: ClientEvent[] = []
  let pendingCursor: ClientEvent | null = null

  function setStatus(s: WsStatus) {
    onStatus?.(s)
  }

  function url(): string | null {
    const token = getAccessToken()
    if (!token) return null
    return `${wsBaseUrl()}/ws/projects/${encodeURIComponent(projectId)}/?token=${encodeURIComponent(token)}`
  }

  function flush() {
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    for (const ev of buffer) socket.send(JSON.stringify(ev))
    buffer.length = 0
    if (pendingCursor) {
      socket.send(JSON.stringify(pendingCursor))
      pendingCursor = null
    }
  }

  function startHeartbeat() {
    stopHeartbeat()
    heartbeatTimer = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'heartbeat' } satisfies ClientEvent))
      }
    }, HEARTBEAT_MS)
  }

  function stopHeartbeat() {
    if (heartbeatTimer != null) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  function scheduleReconnect() {
    if (closed) return
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      setStatus('closed')
      return
    }
    const delayMs = Math.min(30_000, 1_000 * 2 ** attempt)
    attempt += 1
    setStatus('connecting')
    reconnectTimer = setTimeout(open, delayMs)
  }

  function open() {
    reconnectTimer = null
    if (closed) return
    const target = url()
    if (!target) {
      setStatus('closed')
      return
    }
    setStatus(attempt === 0 ? 'connecting' : 'connecting')
    let ws: WebSocket
    try {
      ws = new WebSocket(target)
    } catch (err) {
      console.warn('[ws] construct failed', err)
      scheduleReconnect()
      return
    }
    socket = ws

    ws.onopen = () => {
      attempt = 0
      consecutive4401 = 0
      setStatus('open')
      startHeartbeat()
      flush()
    }

    ws.onmessage = (msg) => {
      try {
        const parsed = JSON.parse(msg.data) as ServerEvent
        onEvent(parsed)
      } catch (err) {
        console.warn('[ws] bad message', err)
      }
    }

    ws.onerror = (err) => {
      // onclose follows — keep the noise down to a single warn per close cycle.
      console.warn('[ws] error', err)
    }

    ws.onclose = (ev) => {
      stopHeartbeat()
      socket = null
      if (closed) return

      if (ev.code === 4401) {
        // Token rejected. Refresh and retry — but only twice in a row before
        // giving up so we don't loop on a wedged refresh token.
        consecutive4401 += 1
        if (consecutive4401 > 2) {
          setStatus('closed')
          return
        }
        setStatus('reauth')
        refreshAccessToken().then((ok) => {
          if (closed) return
          if (!ok) {
            setStatus('closed')
            return
          }
          // Don't backoff this case — it's an auth-loop, not a network issue.
          open()
        })
        return
      }

      if (ev.code === 4403) {
        // No project access — terminal.
        setStatus('closed')
        return
      }

      scheduleReconnect()
    }
  }

  function send(ev: ClientEvent): void {
    if (closed) return
    if (ev.type === 'cursor:move') {
      // Always coalesce. If open, send immediately and clear the buffered one.
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(ev))
        pendingCursor = null
      } else {
        pendingCursor = ev
      }
      return
    }
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(ev))
    } else {
      buffer.push(ev)
    }
  }

  function close(): void {
    closed = true
    stopHeartbeat()
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (socket && socket.readyState !== WebSocket.CLOSED) {
      try { socket.close() } catch { /* noop */ }
    }
    socket = null
  }

  open()

  return { send, close }
}
